import React, { useContext, useMemo } from 'react';
import { QuickSearchResult } from '@/api/product/types';
import { ArticleCard, Button } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { Area, PageType } from '@/generated/data.schemas';
import { useGetLayout } from '@/generated/layout';
import { useGetTranslations } from '@/generated/translations';
import styles from './ArticleResults.module.scss';

interface ResultsProps {
	results?: QuickSearchResult;
}

const MAX_DISPLAYED_ARTICLES = 4;

export const ArticleResults: React.FunctionComponent<ResultsProps> = ({ results }) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const { data: layout } = useGetLayout(segmentationId);

	const searchRoute = useMemo(() => {
		if (!results?.phrase) return null;

		const searchUrl = layout?.data.staticLinks?.find((item) => item.pageType === PageType.Search)?.url ?? '';

		return {
			pathname: searchUrl,
			search: `?phrase=${encodeURIComponent(results.phrase)}`,
		};
	}, [layout?.data.staticLinks, results?.phrase]);

	const content = useMemo(() => results?.content?.slice(0, MAX_DISPLAYED_ARTICLES), [results?.content]);

	if (!content?.length) {
		return null;
	}

	const hasMoreResults = (results?.totalContentHits ?? 0) > MAX_DISPLAYED_ARTICLES;

	return (
		<div className={styles.wrapper}>
			<div className={styles.titleWrapper}>
				<h3 className={styles.title}>{translations?.data.shared.quicksearch.articles}</h3>
				{hasMoreResults && searchRoute && (
					<Button
						className={styles.showMore}
						variant={'linkButton'}
						to={searchRoute}
						state={{ area: Area.StaticPages, pageType: PageType.Search }}
					>
						{translations?.data.shared.quicksearch.showAll}
					</Button>
				)}
			</div>
			<ul className={styles.list}>
				{content.map((article, index) => (
					<li key={article.contentId ?? index}>
						<ArticleCard article={article} isQuickSearch />
					</li>
				))}
			</ul>
		</div>
	);
};
